/**
 * Recognizers for this app's conflict `FetchErrorLike`s — a pure, dependency-free
 * module so ANY layer can import it (stores, components, other pure modules).
 *
 * Why this is NOT in `mutations.ts` (fix round 2 plan, Decision 4): the vitest
 * harnesses under `web/app-home/src/__tests__/playbooks-*.test.tsx` mock
 * `mutations` with an EXPLICIT member list (e.g. `playbooks-playbook-mode.test.tsx`
 * spells out `isSharedMappingConflictError: () => false`). A new export added to
 * `mutations.ts` would therefore be `undefined` inside every such file, and the
 * first call through a catch path — `playbook-draft-store.ts`'s 409 retry — would
 * throw `is not a function` instead of classifying the error. A separate module
 * is never in those mock lists, so it resolves to the real implementation
 * everywhere.
 *
 * `isSharedMappingConflictError` deliberately STAYS in `mutations.ts` for the
 * mirror-image reason: it is already named in those mock lists, so moving it
 * would break the same harnesses this module exists to avoid breaking. Two
 * homes is the cost of that constraint; each is stable where it is.
 */

/**
 * True when `error` is the server's 409 `version_conflict` — the shape
 * `_shared/fetch-error.ts` throws, carrying the problem body's `code`.
 *
 * Structural, not `instanceof`: the error crosses the `apiFetch` boundary as a
 * plain object literal (`FetchErrorLike`), so there is no class to test
 * against. `typeof null === 'object'` is why the `!== null` guard is load
 * bearing, and the `'code' in error` narrowing is what keeps a bare string
 * `'version_conflict'` (not an error at all) from matching.
 */
export function isVersionConflictError(error: unknown): boolean {
  return (
    typeof error === 'object' &&
    error !== null &&
    'code' in error &&
    (error as { code?: unknown }).code === 'version_conflict'
  );
}
